// Libraries
import { createContext, useState, useContext, useRef, ReactNode, ComponentProps } from "react";

// Components
import { AlertDialog } from "components/common/alertDialog";

type AlertDialogProps = ComponentProps<typeof AlertDialog>;

type AlertConfig = Pick<AlertDialogProps, "title" | "type" | "actions" | "isOpen"> & {
  message: ReactNode;
  /** Which `showAlertDialog` call this alert came from; 0 when none is open. */
  id: number;
};

interface AlertDialogContextProps {
  /** Shows the alert, replacing any open one, and returns its id for `hideAlertDialog`. */
  showAlertDialog: (config: Omit<AlertConfig, "isOpen" | "id">) => number;
  /** Closes the open alert, or with an id, only the alert that call opened. */
  hideAlertDialog: (id?: number) => void;
}

const AlertDialogContext = createContext<AlertDialogContextProps>({
  showAlertDialog: () => 0,
  hideAlertDialog: () => {},
});

const CLOSED_ALERT: AlertConfig = {
  isOpen: false,
  type: "warning",
  title: "",
  message: "",
  actions: {},
  id: 0,
};

export const AlertDialogProvider = ({ children }: { children: ReactNode }) => {
  const [alertConfig, setAlertConfig] = useState<AlertConfig>(CLOSED_ALERT);
  const lastAlertIdRef = useRef(0);

  const showAlertDialog: AlertDialogContextProps["showAlertDialog"] = (config) => {
    lastAlertIdRef.current += 1;
    const id = lastAlertIdRef.current;
    setAlertConfig({ type: "warning", ...config, isOpen: true, id });
    return id;
  };

  const hideAlertDialog: AlertDialogContextProps["hideAlertDialog"] = (id) => {
    setAlertConfig((current) => (id === undefined || current.id === id ? CLOSED_ALERT : current));
  };

  return (
    <AlertDialogContext.Provider value={{ showAlertDialog, hideAlertDialog }}>
      {children}
      <AlertDialog
        isOpen={alertConfig.isOpen}
        onClose={() => hideAlertDialog()}
        type={alertConfig.type}
        title={alertConfig.title}
        actions={alertConfig.actions}
      >
        {alertConfig.message}
      </AlertDialog>
    </AlertDialogContext.Provider>
  );
};

export const useAlertDialog = () => useContext(AlertDialogContext);
